import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { getServices, getSettings, createLead } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { useLanguage } from '../lib/LanguageContext';

export default function ContactPage() {
  const [services, setServices] = useState([]);
  const [settings, setSettings] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service_id: '',
    message: '',
  });

  // Helper function to get localized service field
  const getLocalizedField = (service, field) => {
    const enField = `${field}_en`;
    const deField = `${field}_de`;
    if (language === 'en' && service[enField]) {
      return service[enField];
    }
    return service[deField] || service[field] || '';
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, settingsRes] = await Promise.all([
          getServices(),
          getSettings()
        ]);
        setServices(servicesRes.data);
        setSettings(settingsRes.data);
      } catch (err) {
        console.error('Error fetching data:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await createLead({
        ...formData,
        service_id: formData.service_id || null,
      });
      toast.success(t('contactPage.successMessage'));
      setFormData({
        name: '',
        email: '',
        phone: '',
        service_id: '',
        message: '',
      });
    } catch (err) {
      console.error('Error submitting form:', err);
      toast.error(t('contactPage.errorMessage'));
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-24 lg:py-32 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1423666639041-f56000c27a9a?w=1920&q=80" 
            alt="Kontakt"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0A0A0A]/85" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <span className="text-[#D4B982] text-sm font-medium uppercase tracking-widest">{t('contactPage.subtitle')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mt-4 mb-6">
            {t('contactPage.title1')} <span className="text-[#D4B982]">{t('contactPage.title2')}</span>
          </h1>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            {t('contactPage.description')}
          </p>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-6">{t('contactPage.infoTitle')}</h2>
              <p className="text-[#71717A] mb-8">
                {t('contactPage.infoDescription')}
              </p>

              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4B982]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Phone className="text-[#D4B982]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A] mb-1">{t('contactPage.phone')}</h3>
                    <a 
                      href={`tel:${settings.phone || '0176 34681594'}`}
                      className="text-[#71717A] hover:text-[#D4B982] transition-colors"
                    >
                      {settings.phone || '0176 34681594'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4B982]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Mail className="text-[#D4B982]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A] mb-1">{t('contactPage.email')}</h3>
                    <a 
                      href={`mailto:${settings.email || 'info-adservice@gmx.de'}`}
                      className="text-[#71717A] hover:text-[#D4B982] transition-colors"
                    >
                      {settings.email || 'info-adservice@gmx.de'}
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4B982]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <MapPin className="text-[#D4B982]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A] mb-1">{t('contactPage.location')}</h3>
                    <p className="text-[#71717A]">{settings.address || 'Deutschland'}</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-[#D4B982]/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <Clock className="text-[#D4B982]" size={20} />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0A0A0A] mb-1">{t('contactPage.availability')}</h3>
                    <p className="text-[#71717A]">{t('contactPage.availabilityHours')}</p>
                  </div>
                </div>
              </div>

              {/* Trust Badge */}
              <div className="mt-12 p-6 bg-[#F4F4F5] rounded-sm">
                <p className="text-sm text-[#71717A] italic">
                  "{settings.slogan || 'Zuverlässig, schnell und sauber.'}"
                </p>
                <p className="text-[#D4B982] font-semibold mt-2">— A&D Service</p>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-[#F4F4F5] p-8 lg:p-10 rounded-sm">
                <h2 className="text-2xl font-bold text-[#0A0A0A] mb-2">{t('contactPage.formTitle')}</h2>
                <p className="text-[#71717A] mb-8">
                  {t('contactPage.formDescription')}
                </p>

                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-[#0A0A0A]">{t('form.name')} *</Label>
                      <Input
                        id="name"
                        type="text"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        className="mt-2 bg-white border-[#E4E4E7]"
                        placeholder={language === 'en' ? 'John Doe' : 'Max Mustermann'}
                        data-testid="contact-name"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-[#0A0A0A]">{t('form.email')} *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        required
                        className="mt-2 bg-white border-[#E4E4E7]"
                        placeholder={language === 'en' ? 'john@example.com' : 'max@beispiel.de'}
                        data-testid="contact-email"
                      />
                    </div>
                  </div>

                  <div className="grid sm:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-[#0A0A0A]">{t('form.phone')} *</Label>
                      <Input
                        id="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        required
                        className="mt-2 bg-white border-[#E4E4E7]"
                        placeholder="0123 456789"
                        data-testid="contact-phone"
                      />
                    </div>

                    <div>
                      <Label htmlFor="service" className="text-[#0A0A0A]">{t('contactPage.selectService')}</Label>
                      <Select 
                        value={formData.service_id} 
                        onValueChange={(value) => setFormData({ ...formData, service_id: value })}
                      >
                        <SelectTrigger className="mt-2 bg-white border-[#E4E4E7]" data-testid="contact-service">
                          <SelectValue placeholder={t('contactPage.selectPlaceholder')} />
                        </SelectTrigger>
                        <SelectContent>
                          {services.map((service) => (
                            <SelectItem key={service.id} value={service.id}>
                              {getLocalizedField(service, 'title')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-[#0A0A0A]">{t('form.message')} *</Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      required
                      rows={5}
                      className="mt-2 bg-white border-[#E4E4E7]"
                      placeholder={t('contactPage.messagePlaceholder')}
                      data-testid="contact-message"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto bg-[#D4B982] hover:bg-[#C5A059] text-[#0A0A0A] font-semibold px-8 py-6 text-sm uppercase tracking-wider"
                    data-testid="contact-submit"
                  >
                    {isSubmitting ? (
                      t('contactPage.submitting')
                    ) : (
                      <>
                        <Send size={18} className="mr-2" />
                        {t('contactPage.submitButton')}
                      </>
                    )}
                  </Button>

                  <p className="text-xs text-[#71717A]">
                    {t('contactPage.requiredFields')}
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
